(() => {
  if (window.PetomgLockedCartBundles) return;

  const ACTIVE_ATTRIBUTE = 'data-bundle-active';
  const HIDDEN_ATTRIBUTE = 'data-locked-bundle-hidden';
  const groupsByCart = new WeakMap();
  const observedCarts = new WeakSet();
  const observersByCart = new WeakMap();
  const pendingHydration = new WeakSet();

  function restoreRow(row) {
    row.classList.remove(
      'cart-item--locked-bundle-parent',
      'cart-item--locked-bundle-child'
    );
    row.removeAttribute(ACTIVE_ATTRIBUTE);
    row.querySelectorAll(`[${HIDDEN_ATTRIBUTE}]`).forEach((control) => {
      control.hidden = false;
      control.removeAttribute(HIDDEN_ATTRIBUTE);
    });
  }

  function parsedRow(row) {
    const input = row.querySelector('.qty-input__input');
    const parentKey = row.dataset.parentLineKey || '';

    return {
      row,
      id: row.dataset.bundleId || '',
      policy: row.dataset.bundlePolicy || '',
      schema: row.dataset.bundleSchema || '',
      ratio: parentKey ? Number(row.dataset.bundleRatio) : 1,
      key: row.dataset.lineKey || '',
      parentKey,
      line: Number(input?.dataset.index),
      quantity: Number(input?.value),
      input
    };
  }

  function validGroup(lines) {
    const parents = lines.filter((line) => !line.parentKey);
    const children = lines.filter((line) => line.parentKey);
    const keys = new Set(lines.map((line) => line.key));

    if (parents.length !== 1 || children.length < 1 || keys.size !== lines.length) return false;
    if (lines.some((line) => line.policy !== 'locked' || line.schema !== '1')) return false;
    if (lines.some((line) => !line.key || !line.input || !Number.isInteger(line.line) || line.line < 1)) return false;
    if (!Number.isInteger(parents[0].quantity) || parents[0].quantity < 0) return false;
    if (children.some((line) => line.parentKey !== parents[0].key)) return false;
    if (children.some((line) => !Number.isInteger(line.ratio) || line.ratio < 1)) return false;
    if (children.some((line) => line.quantity !== parents[0].quantity * line.ratio)) return false;
    return true;
  }

  function activateGroup(lines) {
    const parent = lines.find((line) => !line.parentKey);
    const children = lines.filter((line) => line.parentKey);

    parent.row.classList.add('cart-item--locked-bundle-parent');
    parent.row.setAttribute(ACTIVE_ATTRIBUTE, 'true');

    children.forEach((child) => {
      child.row.classList.add('cart-item--locked-bundle-child');
      child.row.setAttribute(ACTIVE_ATTRIBUTE, 'true');
      child.row.querySelectorAll('quantity-input, .js-remove-item').forEach((control) => {
        control.hidden = true;
        control.setAttribute(HIDDEN_ATTRIBUTE, 'true');
      });
    });

    return { id: parent.id, parent, children };
  }

  function hydrate(cartItems) {
    if (!cartItems?.isConnected) return;

    const rows = Array.from(cartItems.querySelectorAll('.cart-item'));
    rows.forEach(restoreRow);

    const candidates = rows.map(parsedRow).filter((line) => line.id);
    const grouped = candidates.reduce((groups, line) => {
      if (!groups.has(line.id)) groups.set(line.id, []);
      groups.get(line.id).push(line);
      return groups;
    }, new Map());
    const activeGroups = new Map();

    grouped.forEach((lines, id) => {
      if (validGroup(lines)) activeGroups.set(id, activateGroup(lines));
    });

    groupsByCart.set(cartItems, activeGroups);
  }

  function scheduleHydration(cartItems) {
    if (!cartItems || pendingHydration.has(cartItems)) return;
    pendingHydration.add(cartItems);
    queueMicrotask(() => {
      pendingHydration.delete(cartItems);
      hydrate(cartItems);
    });
  }

  function observeCart(cartItems) {
    if (!cartItems || observedCarts.has(cartItems)) return;
    observedCarts.add(cartItems);
    const observer = new MutationObserver(() => scheduleHydration(cartItems));
    observer.observe(cartItems, {
      childList: true,
      subtree: true
    });
    observersByCart.set(cartItems, observer);
    scheduleHydration(cartItems);
  }

  function disconnectRemovedCarts(node) {
    if (!(node instanceof Element)) return;
    const removedCarts = node.matches('cart-items')
      ? [node]
      : Array.from(node.querySelectorAll?.('cart-items') || []);
    removedCarts.forEach((cartItems) => {
      observersByCart.get(cartItems)?.disconnect();
      observersByCart.delete(cartItems);
      observedCarts.delete(cartItems);
      groupsByCart.delete(cartItems);
      pendingHydration.delete(cartItems);
    });
  }

  function observeAddedCarts() {
    document.querySelectorAll('cart-items').forEach(observeCart);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach(disconnectRemovedCarts);
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('cart-items')) observeCart(node);
          node.querySelectorAll?.('cart-items').forEach(observeCart);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function activeContext(target) {
    const row = target.closest?.(`.cart-item[${ACTIVE_ATTRIBUTE}="true"]`);
    const cartItems = row?.closest('cart-items');
    const group = cartItems ? groupsByCart.get(cartItems)?.get(row.dataset.bundleId) : null;
    return row && cartItems && group ? { row, cartItems, group } : null;
  }

  function lineChanges(group, parentQuantity) {
    return [group.parent, ...group.children].map((line) => ({
      key: line.key,
      quantity: parentQuantity * line.ratio
    }));
  }

  function restoreChildInput(context) {
    const child = context.group.children.find((line) => line.row === context.row);
    if (!child?.input) return;
    const expected = context.group.parent.quantity * child.ratio;
    child.input.value = String(expected);
    const quantityInput = child.input.closest('quantity-input');
    if (quantityInput) quantityInput.currentQty = String(expected);
  }

  function handleChange(event) {
    if (!event.target.matches?.('.qty-input__input')) return;
    const context = activeContext(event.target);
    if (!context) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (context.row.dataset.parentLineKey) {
      restoreChildInput(context);
      return;
    }

    const targetQuantity = Number(event.target.value);
    if (!Number.isInteger(targetQuantity) || targetQuantity < 0) {
      event.target.value = event.target.dataset.initialValue;
      return;
    }

    void context.cartItems.updateQuantity(
      context.group.parent.line,
      targetQuantity,
      document.activeElement?.name,
      lineChanges(context.group, targetQuantity)
    );
  }

  function handleClick(event) {
    const removeButton = event.target.closest?.('.js-remove-item');
    if (!removeButton) return;
    const context = activeContext(removeButton);
    if (!context) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (context.row.dataset.parentLineKey) return;

    // Shopify removes native nested children when their parent line is removed.
    void context.cartItems.updateQuantity(context.group.parent.line, 0);
  }

  function init() {
    observeAddedCarts();
    document.addEventListener('change', handleChange, true);
    document.addEventListener('click', handleClick, true);
  }

  window.PetomgLockedCartBundles = { hydrate, validGroup };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
